import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, Newspaper } from "lucide-react";
import { api, resolveImg } from "@/lib/api";

const fmtDate = (s) => {
  if (!s) return "";
  const d = new Date(s);
  return isNaN(d) ? "" : d.toLocaleDateString("ro-RO", { day: "2-digit", month: "long", year: "numeric" });
};

export default function LatestArticle() {
  const [articles, setArticles] = useState([]);
  const [cfg, setCfg] = useState({ mode: "latest", article_id: "", rotate_seconds: 8 });
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api.get("/articles").then((r) => setArticles(r.data || [])).catch(() => {});
    api
      .get("/content")
      .then((r) => setCfg(r.data?.settings?.featured || { mode: "latest", article_id: "", rotate_seconds: 8 }))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (cfg.mode !== "rotate" || articles.length <= 1) return;
    const ms = Math.max(3, Number(cfg.rotate_seconds) || 8) * 1000;
    const t = setInterval(() => setIdx((i) => (i + 1) % articles.length), ms);
    return () => clearInterval(t);
  }, [cfg, articles]);

  if (!articles.length) return null;

  let article;
  if (cfg.mode === "fixed") article = articles.find((a) => a.id === cfg.article_id) || articles[0];
  else if (cfg.mode === "rotate") article = articles[idx % articles.length];
  else article = articles[0];

  return (
    <section className="bg-[#F4F6F9] py-10 lg:py-12" data-testid="latest-article-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Newspaper className="w-5 h-5 text-[#FF5A1F]" />
            <p className="text-sm font-bold uppercase tracking-wider text-[#FF5A1F]">
              {cfg.mode === "rotate" ? "Din blogul nostru" : "Ultimul articol din blog"}
            </p>
          </div>
          <Link
            to="/articole"
            data-testid="latest-article-all-link"
            className="text-sm font-semibold text-slate-500 hover:text-[#FF5A1F] inline-flex items-center gap-1.5 transition-colors duration-200"
          >
            Vezi toate articolele <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <Link
          key={article.id}
          to={`/articole/${article.slug}`}
          data-testid="latest-article-card"
          className="group grid grid-cols-1 lg:grid-cols-2 bg-white rounded-3xl overflow-hidden border border-[#E2E8F0] hover:shadow-xl transition-shadow duration-300 animate-in fade-in duration-500"
        >
          <div className="aspect-[16/9] lg:aspect-auto lg:h-[300px] bg-[#E2E8F0] overflow-hidden">
            {article.cover_image ? (
              <img
                src={resolveImg(article.cover_image)}
                alt={article.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Newspaper className="w-12 h-12 text-slate-300" />
              </div>
            )}
          </div>
          <div className="p-6 sm:p-8 flex flex-col justify-center">
            <div className="flex items-center gap-1.5 text-xs text-slate-400 mb-2">
              <CalendarDays className="w-3.5 h-3.5" />
              {fmtDate(article.created_at)}
            </div>
            <h2 className="font-display text-xl sm:text-2xl font-bold text-[#0F1E36] leading-tight group-hover:text-[#FF5A1F] transition-colors duration-200">
              {article.title}
            </h2>
            <p className="text-sm sm:text-base text-slate-500 mt-3 line-clamp-3">{article.excerpt}</p>
            <span className="inline-flex items-center gap-2 mt-4 text-[#FF5A1F] font-bold">
              Citește mai departe
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
            </span>
          </div>
        </Link>

        {cfg.mode === "rotate" && articles.length > 1 && (
          <div className="flex items-center justify-center gap-2 mt-5">
            {articles.map((a, i) => (
              <button
                key={a.id}
                type="button"
                aria-label={`Articol ${i + 1}`}
                data-testid={`featured-dot-${i}`}
                onClick={() => setIdx(i)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  i === idx % articles.length ? "w-6 bg-[#FF5A1F]" : "w-2 bg-slate-300 hover:bg-slate-400"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
